import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHero from "./PageHero";
import { Reveal, SectionEyebrow, ArrowIcon } from "../components/common/Section";
import { SkeletonRows } from "../components/common/Skeleton";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";

const CareersPage = () => {
  const { t, pick, lang } = useI18n();
  const [openings, setOpenings] = useState([]);
  const [openingsLoaded, setOpeningsLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", resume_url: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/public/careers")
      .then((r) => setOpenings(r.data))
      .catch(() => setOpenings([]))
      .finally(() => setOpeningsLoaded(true));
  }, []);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/career-applications", {
        ...form,
        opening_id: selected?.id || null,
        position: selected ? pick(selected.title, form.position) : form.position,
        lang,
      });
      toast.success(t("common.applyReceived"));
      setForm({ name: "", email: "", phone: "", position: "", resume_url: "", message: "" });
      setSelected(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : err.message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-4 text-brand-dark placeholder-brand-medium transition-colors";

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow={t("nav.careers")}
        title={{
          tr: "Sınırların ötesinde projeler için ekibimize katılın.",
          en: "Join our team to build projects beyond borders.",
        }}
        image="https://images.unsplash.com/photo-1600068485426-4d32cd411697"
      />

      <section className="bg-white">
        <div className="container-x py-24 md:py-32">
          <Reveal>
            <SectionEyebrow>{lang === "tr" ? "Açık Pozisyonlar" : "Open Positions"}</SectionEyebrow>
            <h2 className="display display-md mt-6 max-w-2xl">
              {lang === "tr" ? "Bugün başvurun, yarına iz bırakın." : "Apply today, shape tomorrow."}
            </h2>
          </Reveal>

          {!openingsLoaded ? (
            <div className="mt-16">
              <SkeletonRows count={4} testId="careers-openings-loading" />
            </div>
          ) : (
            <div className="mt-16 divide-y divide-black/10 border-t border-b border-black/10">
              {openings.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => setSelected(o)}
                  className="w-full flex flex-col md:flex-row md:items-center gap-2 md:gap-8 py-8 text-left group"
                  data-testid={`opening-${o.slug}`}
                >
                  <div className="flex-1">
                    <div className="font-serif text-2xl md:text-3xl text-brand-navy group-hover:text-brand-primary transition-colors">
                      {pick(o.title, "")}
                    </div>
                    <div className="mt-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">
                      {o.department} · {o.location} · {o.employment_type}
                    </div>
                  </div>
                  <div className="link-gold shrink-0">
                    <span>{t("common.applyNow")}</span>
                    <ArrowIcon />
                  </div>
                </button>
              ))}
              {!openings.length ? (
                <div className="py-16 text-brand-medium" data-testid="careers-empty">
                  {lang === "tr" ? "Şu anda açık pozisyon bulunmuyor. Genel başvuru için aşağıdaki formu doldurabilirsiniz." : "No open positions at the moment. Feel free to send a general application below."}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <section className="bg-brand-light">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>{lang === "tr" ? "Başvuru Formu" : "Application Form"}</SectionEyebrow>
            <h3 className="display display-md mt-6">
              {selected ? pick(selected.title, "") : lang === "tr" ? "Genel Başvuru" : "General Application"}
            </h3>
            {selected ? (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="mt-4 text-xs uppercase tracking-[0.22em] text-brand-medium underline"
                data-testid="clear-opening"
              >
                {lang === "tr" ? "Pozisyonu Değiştir" : "Change Position"}
              </button>
            ) : null}
          </div>
          <form onSubmit={submit} className="lg:col-span-7" data-testid="career-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <input required className={inputCls} placeholder={t("forms.name")} value={form.name} onChange={change("name")} data-testid="career-input-name" />
              <input required type="email" className={inputCls} placeholder={t("forms.email")} value={form.email} onChange={change("email")} data-testid="career-input-email" />
              <input className={inputCls} placeholder={t("forms.phone")} value={form.phone} onChange={change("phone")} data-testid="career-input-phone" />
              <input className={inputCls} placeholder={t("forms.position")} value={selected ? pick(selected.title, "") : form.position} onChange={change("position")} readOnly={!!selected} data-testid="career-input-position" />
              <input className={`${inputCls} md:col-span-2`} placeholder={t("forms.resumeUrl")} value={form.resume_url} onChange={change("resume_url")} data-testid="career-input-resume" />
              <textarea rows={4} className={`${inputCls} md:col-span-2 resize-none`} placeholder={t("forms.message")} value={form.message} onChange={change("message")} data-testid="career-input-message" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary mt-10 disabled:opacity-60" data-testid="career-submit-btn">
              <span>{busy ? t("common.loading") : t("common.applyNow")}</span>
              <ArrowIcon />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
